/**
 * TextToSpeechService - Advanced Text-to-Speech Service
 * Based on Android: services/voice/TextToSpeechService.kt
 *
 * Features:
 * - High-quality speech synthesis using Web Speech API
 * - Multiple voice selection
 * - Speech rate and pitch control
 * - Utterance queuing
 * - Progress tracking
 * - SSML support (basic)
 */

import type {
  TTSState,
  SpeechEvent,
  VoiceInfo,
  SpeechConfig,
  SpeakResult,
} from './voice-types'
import { QueueMode, defaultSpeechConfig } from './voice-types'

export class TextToSpeechService {
  private synth: SpeechSynthesis | null = null
  private utteranceCounter = 0
  private utteranceCallbacks: Map<string, { resolve: (value: boolean) => void }> = new Map()
  private currentUtterance: SpeechSynthesisUtterance | null = null

  private state: TTSState = {
    isInitialized: false,
    isSpeaking: false,
    isPaused: false,
    currentLanguage: 'en-US',
    currentVoice: null,
    speechRate: 1,
    pitch: 1,
    queueSize: 0,
    availableVoices: 0,
  }

  private listeners: Set<(state: TTSState) => void> = new Set()
  private eventListeners: Set<(event: SpeechEvent) => void> = new Set()

  constructor() {
    if (typeof window !== 'undefined') {
      this.initialize()
    }
  }

  // ============================================================================
  // State Management
  // ============================================================================

  getState(): TTSState {
    return { ...this.state }
  }

  subscribe(listener: (state: TTSState) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  subscribeToEvents(listener: (event: SpeechEvent) => void): () => void {
    this.eventListeners.add(listener)
    return () => this.eventListeners.delete(listener)
  }

  private setState(updates: Partial<TTSState>): void {
    this.state = { ...this.state, ...updates }
    this.listeners.forEach((listener) => listener(this.getState()))
  }

  private emitEvent(event: SpeechEvent): void {
    this.eventListeners.forEach((listener) => listener(event))
  }

  // ============================================================================
  // Initialization
  // ============================================================================

  async initialize(): Promise<boolean> {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      console.warn('[TTS] Speech synthesis not supported')
      return false
    }

    this.synth = window.speechSynthesis

    // Wait for voices to load
    await this.waitForVoices()

    const voices = this.synth.getVoices()
    this.setState({
      isInitialized: true,
      availableVoices: voices.length,
    })

    console.log(`[TTS] Initialized with ${voices.length} voices`)
    return true
  }

  private waitForVoices(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.synth) {
        resolve()
        return
      }

      const voices = this.synth.getVoices()
      if (voices.length > 0) {
        resolve()
        return
      }

      // Wait for voiceschanged event
      const onVoicesChanged = () => {
        window.speechSynthesis.removeEventListener('voiceschanged', onVoicesChanged)
        resolve()
      }

      window.speechSynthesis.addEventListener('voiceschanged', onVoicesChanged)

      // Timeout fallback
      setTimeout(() => {
        window.speechSynthesis.removeEventListener('voiceschanged', onVoicesChanged)
        resolve()
      }, 1000)
    })
  }

  // ============================================================================
  // Speaking
  // ============================================================================

  /**
   * Speak text
   */
  speak(text: string, config: Partial<SpeechConfig> = {}): SpeakResult {
    if (!this.synth) {
      return { success: false, utteranceId: null, error: 'TTS not initialized' }
    }

    const fullConfig = { ...defaultSpeechConfig, ...config }
    const utteranceId = `utterance_${++this.utteranceCounter}`

    // Handle queue mode
    if (fullConfig.queueMode === QueueMode.FLUSH) {
      this.stop()
    }

    const utterance = new SpeechSynthesisUtterance(text)

    // Apply configuration
    utterance.lang = fullConfig.language
    utterance.rate = Math.max(0.1, Math.min(10, fullConfig.speechRate))
    utterance.pitch = Math.max(0, Math.min(2, fullConfig.pitch))
    utterance.volume = Math.max(0, Math.min(1, fullConfig.volume))

    // Set voice if specified
    if (fullConfig.voiceName) {
      const voice = this.synth.getVoices().find((v) => v.name === fullConfig.voiceName)
      if (voice) {
        utterance.voice = voice
      }
    } else {
      // Find best voice for language
      const voice = this.findVoiceForLanguage(fullConfig.language)
      if (voice) {
        utterance.voice = voice
      }
    }

    // Event handlers
    utterance.onstart = () => {
      this.currentUtterance = utterance
      this.setState({ isSpeaking: true })
      this.emitEvent({ type: 'started', utteranceId })
    }

    utterance.onend = () => {
      this.currentUtterance = null
      this.setState({
        isSpeaking: this.synth?.speaking || false,
        queueSize: Math.max(0, this.state.queueSize - 1),
      })
      this.emitEvent({ type: 'done', utteranceId })

      // Resolve callback
      const callback = this.utteranceCallbacks.get(utteranceId)
      if (callback) {
        callback.resolve(true)
        this.utteranceCallbacks.delete(utteranceId)
      }
    }

    utterance.onerror = (event) => {
      this.currentUtterance = null
      this.setState({ isSpeaking: false })
      this.emitEvent({ type: 'error', utteranceId, errorCode: event.error ? -1 : 0 })

      // Resolve callback with failure
      const callback = this.utteranceCallbacks.get(utteranceId)
      if (callback) {
        callback.resolve(false)
        this.utteranceCallbacks.delete(utteranceId)
      }
    }

    utterance.onboundary = (event) => {
      if (event.name === 'word') {
        this.emitEvent({
          type: 'progress',
          utteranceId,
          charIndex: event.charIndex,
          charLength: event.charLength || 0,
        })
      }
    }

    // Speak
    this.synth.speak(utterance)
    this.setState({
      queueSize: this.state.queueSize + 1,
      currentLanguage: fullConfig.language,
      speechRate: fullConfig.speechRate,
      pitch: fullConfig.pitch,
    })

    return { success: true, utteranceId, error: null }
  }

  /**
   * Speak text and wait for completion
   */
  async speakAndWait(text: string, config: Partial<SpeechConfig> = {}): Promise<boolean> {
    const result = this.speak(text, config)

    if (!result.success || !result.utteranceId) {
      return false
    }

    return new Promise((resolve) => {
      this.utteranceCallbacks.set(result.utteranceId!, { resolve })

      // Timeout after 60 seconds
      setTimeout(() => {
        const callback = this.utteranceCallbacks.get(result.utteranceId!)
        if (callback) {
          callback.resolve(false)
          this.utteranceCallbacks.delete(result.utteranceId!)
        }
      }, 60000)
    })
  }

  /**
   * Speak SSML content (basic support)
   */
  speakSSML(ssml: string, config: Partial<SpeechConfig> = {}): SpeakResult {
    // Web Speech API doesn't support SSML directly, so extract plain text
    const plainText = ssml.replace(/<[^>]*>/g, '').trim()
    return this.speak(plainText, config)
  }

  /**
   * Stop speaking
   */
  stop(): void {
    if (!this.synth) return

    this.synth.cancel()
    this.currentUtterance = null

    this.setState({
      isSpeaking: false,
      isPaused: false,
      queueSize: 0,
    })

    // Complete all pending callbacks
    this.utteranceCallbacks.forEach((callback) => callback.resolve(false))
    this.utteranceCallbacks.clear()
  }

  /**
   * Pause speaking
   */
  pause(): boolean {
    if (!this.synth || !this.state.isSpeaking) return false

    this.synth.pause()
    this.setState({ isPaused: true, isSpeaking: false })
    this.emitEvent({ type: 'paused' })
    return true
  }

  /**
   * Resume speaking
   */
  resume(): boolean {
    if (!this.synth || !this.state.isPaused) return false

    this.synth.resume()
    this.setState({ isPaused: false, isSpeaking: true })
    this.emitEvent({ type: 'resumed' })
    return true
  }

  // ============================================================================
  // Voice Management
  // ============================================================================

  /**
   * Get available voices
   */
  getAvailableVoices(): VoiceInfo[] {
    if (!this.synth) return []

    return this.synth.getVoices().map((voice) => ({
      name: voice.name,
      lang: voice.lang,
      localService: voice.localService,
      default: voice.default,
    }))
  }

  /**
   * Get voices for a specific language
   */
  getVoicesForLanguage(languageCode: string): VoiceInfo[] {
    const allVoices = this.getAvailableVoices()
    const langPrefix = languageCode.split('-')[0].toLowerCase()

    return allVoices.filter((voice) => {
      const voiceLang = voice.lang.toLowerCase()
      return voiceLang === languageCode.toLowerCase() || voiceLang.startsWith(langPrefix)
    })
  }

  /**
   * Find best voice for language
   */
  private findVoiceForLanguage(languageCode: string): SpeechSynthesisVoice | null {
    if (!this.synth) return null

    const voices = this.synth.getVoices()
    const langPrefix = languageCode.split('-')[0].toLowerCase()

    // Try exact match first
    let voice = voices.find((v) => v.lang.toLowerCase() === languageCode.toLowerCase())
    if (voice) return voice

    // Try language prefix match
    voice = voices.find((v) => v.lang.toLowerCase().startsWith(langPrefix))
    if (voice) return voice

    // Fall back to default
    return voices.find((v) => v.default) || voices[0] || null
  }

  /**
   * Set voice by name
   */
  setVoice(voiceName: string): boolean {
    if (!this.synth) return false

    const voice = this.synth.getVoices().find((v) => v.name === voiceName)
    if (voice) {
      this.setState({ currentVoice: voiceName })
      return true
    }
    return false
  }

  // ============================================================================
  // Configuration
  // ============================================================================

  /**
   * Set speech rate
   */
  setSpeechRate(rate: number): boolean {
    const clampedRate = Math.max(0.1, Math.min(10, rate))
    this.setState({ speechRate: clampedRate })
    return true
  }

  /**
   * Set pitch
   */
  setPitch(pitch: number): boolean {
    const clampedPitch = Math.max(0, Math.min(2, pitch))
    this.setState({ pitch: clampedPitch })
    return true
  }

  /**
   * Set language
   */
  setLanguage(languageCode: string): boolean {
    this.setState({ currentLanguage: languageCode })
    return true
  }

  // ============================================================================
  // Status
  // ============================================================================

  /**
   * Check if currently speaking
   */
  isSpeaking(): boolean {
    return this.synth?.speaking || false
  }

  /**
   * Check if paused
   */
  isPaused(): boolean {
    return this.synth?.paused || false
  }

  /**
   * Check if TTS is supported
   */
  isSupported(): boolean {
    return typeof window !== 'undefined' && 'speechSynthesis' in window
  }

  /**
   * Get supported languages
   */
  getSupportedLanguages(): string[] {
    const voices = this.getAvailableVoices()
    const languages = new Set(voices.map((v) => v.lang))
    return Array.from(languages)
  }

  /**
   * Cleanup
   */
  cleanup(): void {
    this.stop()
    this.listeners.clear()
    this.eventListeners.clear()
    this.utteranceCallbacks.clear()
  }
}

// Singleton instance
let instance: TextToSpeechService | null = null

export function getTextToSpeechService(): TextToSpeechService {
  if (!instance) {
    instance = new TextToSpeechService()
  }
  return instance
}

export default TextToSpeechService
